import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Hero() {
  const { t } = useTranslation('toiture');
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>(0.1);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-[70vh] md:min-h-[75vh] flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Close-up%20macro%20photography%20of%20new%20asphalt%20shingle%20roof%20texture%2C%20sharp%20focus%20on%20overlapping%20grey%20shingles%20with%20warm%20golden%20sunlight%2C%20Quebec%20residential%20roofing%20detail%20shot%2C%20dramatic%20warm%20light%20with%20rich%20shadows%2C%20architectural%20detail%20photography%2C%20professional%20construction%20photography%2C%20warm%20copper%20and%20grey%20tones%2C%20simple%20clean%20sky%20background%2C%20no%20text%20visible&width=1920&height=900&seq=toiture-hero1&orientation=landscape"
          alt="Gros plan toiture neuve bardeau à Lévis par couvreur certifié RBQ"
          className="w-full h-full object-cover"
          loading="eager"
          fetchpriority="high"
          width="1920"
          height="900"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-primary/65 via-slate-primary/45 to-slate-primary/75" />
      </div>

      {/* Content */}
      <div
        ref={ref}
        className={`relative z-10 w-full px-4 md:px-6 lg:px-10 pt-24 pb-16 max-w-5xl mx-auto text-center transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        {/* Breadcrumb */}
        <nav className="mb-6" aria-label="Fil d'Ariane">
          <ol className="flex items-center justify-center gap-2 text-sm text-white/70">
            <li>
              <a href="/" onClick={(e) => { e.preventDefault(); if (window.REACT_APP_NAVIGATE) window.REACT_APP_NAVIGATE('/'); }} className="hover:text-white transition-colors">
                {t('breadcrumb_home')}
              </a>
            </li>
            <li><i className="ri-arrow-right-s-line" /></li>
            <li className="text-white font-medium">{t('breadcrumb_current')}</li>
          </ol>
        </nav>

        {/* H1 */}
        <h1 className="font-manrope font-extrabold text-white text-[40px] sm:text-4xl md:text-5xl lg:text-6xl leading-tight mb-5">
          {t('hero_h1')}
        </h1>

        {/* Subtitle */}
        <p className="text-white/85 text-base md:text-lg lg:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
          {t('hero_subtitle')}
        </p>

        {/* CTA */}
        <button
          onClick={() => scrollToSection('quote-form')}
          className="group bg-copper hover:bg-copper-dark text-white font-semibold px-8 py-4 rounded-md text-base md:text-lg transition-all duration-300 flex items-center gap-3 mx-auto whitespace-nowrap"
        >
          {t('hero_cta')}
          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
        </button>

        {/* Stats */}
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-10">
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <i className="ri-home-smile-line text-copper" />
            <span className="text-white text-sm font-medium">{t('hero_stat_1')}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <div className="flex text-yellow-400 text-xs">
              <i className="ri-star-fill" />
              <i className="ri-star-fill" />
              <i className="ri-star-fill" />
              <i className="ri-star-fill" />
              <i className="ri-star-fill" />
            </div>
            <span className="text-white text-sm font-medium">{t('hero_stat_2')}</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <i className="ri-file-shield-line text-copper" />
            <span className="text-white text-sm font-medium">{t('hero_stat_3')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}